<div class="page-shell">
  <div class="page-header">
    <div>
      <h1>Compare feature flag environments</h1>
      <p>Read-only comparison of Managed Feature Flags between the connected QA and Production environments.</p>
    </div>
    <button mat-stroked-button type="button" (click)="backToFlags()">
      <mat-icon>arrow_back</mat-icon>
      Back to flags
    </button>
  </div>

  @if (state === 'loading') {
    <section class="surface-panel">
      <div class="loading-state"><mat-spinner diameter="28"></mat-spinner><span>Loading environment comparison…</span></div>
    </section>
  }

  @if (state === 'unsupported') {
    <section class="surface-panel state-panel">
      <strong>Environment comparison is available only when connected to QA or Production.</strong>
    </section>
  }

  @if (state === 'unavailable') {
    <section class="surface-panel state-panel error-state">
      <div>
        <strong>Environment comparison is temporarily unavailable.</strong>
        @if (unavailableEnvironments.length) {
          <p>Unavailable: {{ unavailableEnvironmentLabels() }}.</p>
        }
        @if (!unavailableEnvironments.length) {
          <p>One or more comparison environments are unavailable.</p>
        }
      </div>
      <button mat-stroked-button type="button" (click)="loadComparison()">Retry</button>
    </section>
  }

  @if (state === 'authorization') {
    <section class="surface-panel state-panel error-state">
      <strong>You are not authorized to compare Managed Feature Flag environments.</strong>
    </section>
  }

  @if (state === 'error') {
    <section class="surface-panel state-panel error-state">
      <div><strong>Environment comparison is unavailable.</strong><p>Try again after checking the connected backend.</p></div>
      <button mat-stroked-button type="button" (click)="loadComparison()">Retry</button>
    </section>
  }

  @if (state === 'success' && comparison) {
    <p class="environment-note">Comparison returned by {{ environmentLabel(comparison.environments.current) }} for {{ environmentLabel(comparison.environments.qa) }} and {{ environmentLabel(comparison.environments.production) }}.</p>
    <section class="summary-grid" aria-label="Comparison summary">
      <div class="summary-card"><span>Total</span><strong>{{ comparison.summary.total }}</strong></div>
      <div class="summary-card"><span>Same</span><strong>{{ comparison.summary.same }}</strong></div>
      <div class="summary-card"><span>Changed</span><strong>{{ comparison.summary.changed }}</strong></div>
      <div class="summary-card"><span>Only in {{ environmentLabel(comparison.environments.qa) }}</span><strong>{{ comparison.summary.onlyInQa }}</strong></div>
      <div class="summary-card"><span>Only in {{ environmentLabel(comparison.environments.production) }}</span><strong>{{ comparison.summary.onlyInProduction }}</strong></div>
    </section>
    @if (comparison.results.length === 0) {
      <section class="surface-panel">
        <div class="empty-state"><strong>No Managed Feature Flags were returned for comparison.</strong></div>
      </section>
    }
    @if (comparison.results.length > 0) {
      <section class="results-list">
        @for (result of comparison.results; track trackByKey($index, result)) {
          <article class="surface-panel comparison-result">
            <div class="result-header">
              <div><h2>{{ result.key }}</h2><span class="status-pill" [ngClass]="'status-' + result.status">{{ statusLabel(result.status) }}</span></div>
              <button mat-stroked-button type="button" (click)="toggleExpanded(result)" [attr.aria-expanded]="isExpanded(result)">
                <mat-icon>{{ isExpanded(result) ? 'expand_less' : 'expand_more' }}</mat-icon>
                {{ isExpanded(result) ? 'Hide details' : 'View details' }}
              </button>
            </div>
            @if (result.differentFields.length) {
              <p class="difference-summary">Different fields: {{ differentFieldLabels(result) }}</p>
            }
            @if (!result.differentFields.length) {
              <p class="difference-summary">No differing fields reported by the backend.</p>
            }
            @if (isExpanded(result)) {
              <div class="snapshot-columns">
                <section class="snapshot-column">
                  <h3>{{ environmentLabel(comparison.environments.qa) }}</h3>
                  @if (!hasSnapshot(result.qa)) {
                    <p class="not-present">Not present</p>
                  }
                  @if (hasSnapshot(result.qa)) {
                    <dl>
                      <dt>Name</dt><dd>{{ textValue($safeNavigationMigration(result.qa?.name), result.qa) }}</dd>
                      <dt>Description</dt><dd>{{ textValue($safeNavigationMigration(result.qa?.description), result.qa) }}</dd>
                      <dt>App targets</dt><dd>{{ appTargets(result.qa) }}</dd>
                      <dt>Active</dt><dd>{{ booleanValue($safeNavigationMigration(result.qa?.isActive) === undefined ? null : $safeNavigationMigration(result.qa?.isActive)) }}</dd>
                      <dt>Default enabled</dt><dd>{{ booleanValue($safeNavigationMigration(result.qa?.defaultEnabled) === undefined ? null : $safeNavigationMigration(result.qa?.defaultEnabled)) }}</dd>
                      <dt>Included users</dt><dd>{{ values($safeNavigationMigration(result.qa?.userOverrides?.includeUserIds), result.qa) }}</dd>
                      <dt>Excluded users</dt><dd>{{ values($safeNavigationMigration(result.qa?.userOverrides?.excludeUserIds), result.qa) }}</dd>
                      <dt>Schema version</dt><dd>{{ result.qa?.schemaVersion }}</dd>
                    </dl>
                    <div class="rules"><h4>Targeting rules</h4>@if (!rules(result.qa).length) {
                    <p>None</p>
                    }@for (rule of rules(result.qa); track rule; let index = $index) {
                    <div class="rule"><strong>Rule {{ index + 1 }}: {{ rule.effect === 'enable' ? 'Enable' : 'Disable' }}</strong><dl><dt>Include teams</dt><dd>{{ values(rule.teamIds, result.qa) }}</dd><dt>Exclude teams</dt><dd>{{ values(rule.excludeTeamIds, result.qa) }}</dd><dt>Include companies</dt><dd>{{ values(rule.companyIds, result.qa) }}</dd><dt>Exclude companies</dt><dd>{{ values(rule.excludeCompanyIds, result.qa) }}</dd><dt>Include partners</dt><dd>{{ values(rule.partnerIds, result.qa) }}</dd><dt>Exclude partners</dt><dd>{{ values(rule.excludePartnerIds, result.qa) }}</dd><dt>Include states</dt><dd>{{ values(rule.states, result.qa) }}</dd><dt>Exclude states</dt><dd>{{ values(rule.excludeStates, result.qa) }}</dd></dl></div>
                  }</div>
                }
              </section>
              <section class="snapshot-column">
                <h3>{{ environmentLabel(comparison.environments.production) }}</h3>
                @if (!hasSnapshot(result.production)) {
                  <p class="not-present">Not present</p>
                }
                @if (hasSnapshot(result.production)) {
                  <dl>
                    <dt>Name</dt><dd>{{ textValue($safeNavigationMigration(result.production?.name), result.production) }}</dd>
                    <dt>Description</dt><dd>{{ textValue($safeNavigationMigration(result.production?.description), result.production) }}</dd>
                    <dt>App targets</dt><dd>{{ appTargets(result.production) }}</dd>
                    <dt>Active</dt><dd>{{ booleanValue($safeNavigationMigration(result.production?.isActive) === undefined ? null : $safeNavigationMigration(result.production?.isActive)) }}</dd>
                    <dt>Default enabled</dt><dd>{{ booleanValue($safeNavigationMigration(result.production?.defaultEnabled) === undefined ? null : $safeNavigationMigration(result.production?.defaultEnabled)) }}</dd>
                    <dt>Included users</dt><dd>{{ values($safeNavigationMigration(result.production?.userOverrides?.includeUserIds), result.production) }}</dd>
                    <dt>Excluded users</dt><dd>{{ values($safeNavigationMigration(result.production?.userOverrides?.excludeUserIds), result.production) }}</dd>
                    <dt>Schema version</dt><dd>{{ result.production?.schemaVersion }}</dd>
                  </dl>
                  <div class="rules"><h4>Targeting rules</h4>@if (!rules(result.production).length) {
                  <p>None</p>
                  }@for (rule of rules(result.production); track rule; let index = $index) {
                  <div class="rule"><strong>Rule {{ index + 1 }}: {{ rule.effect === 'enable' ? 'Enable' : 'Disable' }}</strong><dl><dt>Include teams</dt><dd>{{ values(rule.teamIds, result.production) }}</dd><dt>Exclude teams</dt><dd>{{ values(rule.excludeTeamIds, result.production) }}</dd><dt>Include companies</dt><dd>{{ values(rule.companyIds, result.production) }}</dd><dt>Exclude companies</dt><dd>{{ values(rule.excludeCompanyIds, result.production) }}</dd><dt>Include partners</dt><dd>{{ values(rule.partnerIds, result.production) }}</dd><dt>Exclude partners</dt><dd>{{ values(rule.excludePartnerIds, result.production) }}</dd><dt>Include states</dt><dd>{{ values(rule.states, result.production) }}</dd><dt>Exclude states</dt><dd>{{ values(rule.excludeStates, result.production) }}</dd></dl></div>
                }</div>
              }
            </section>
          </div>
        }
      </article>
    }
  </section>
}
}
</div>
